import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaRobot, FaDatabase, FaProjectDiagram, FaCodeBranch, FaCheckCircle, FaGithub, FaUser, FaServer, FaCogs, FaRoute, FaLayerGroup } from 'react-icons/fa';
import styles from './CaseStudyModal.module.css';

const CaseStudyModal = ({ isOpen, onClose }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className={styles.overlay} onClick={onClose}>
          <motion.div 
            className={styles.modalContent}
            initial={{ y: 50, opacity: 0, scale: 0.95 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 20, opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.3 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button className={styles.closeBtn} onClick={onClose}>
              <FaTimes />
            </button>

            <div className={styles.modalHeader}>
              <span className={styles.badge}><FaLayerGroup/> System Architecture</span>
              <h2>Resumia — Autonomous Career Intelligence Agent</h2>
              <p>An AI-Powered Resume Intelligence Platform using LangChain, FastAPI & RAG.</p>
            </div>

            <div className={styles.scrollableArea}>
              
              {/* Architecture Flow */}
              <section className={styles.section}>
                <h3><FaProjectDiagram /> Agentic Architecture</h3>
                <div className={styles.flowContainer}>
                  <div className={styles.flowBox}><FaUser style={{ marginRight: '8px' }}/> User (React UI)</div>
                  <div className={styles.flowArrow}>↓</div>
                  <div className={styles.flowBox}><FaServer style={{ marginRight: '8px' }}/> FastAPI Backend <br/><small>Text Extraction & Orchestration</small></div>
                  <div className={styles.flowArrow}>↓</div>
                  <div className={styles.flowGrid}>
                    <div className={styles.flowAgent}><FaRobot style={{ marginRight: '8px' }}/> ParserAgent<br/><small>Extracts plain text to JSON Profile</small></div>
                    <div className={styles.flowAgent}><FaCogs style={{ marginRight: '8px' }}/> ATSAgent<br/><small>Scores resume vs parsed RAG Context</small></div>
                    <div className={styles.flowAgent}><FaRoute style={{ marginRight: '8px' }}/> FeedbackAgent<br/><small>Gen. 30/60/90 Day Roadmap</small></div>
                  </div>
                  <div className={styles.flowArrow}>↑↓</div>
                  <div className={styles.flowBox}><FaDatabase style={{ marginRight: '8px' }}/> FAISS Vector DB<br/><small>Contains Industry JD Context using Gemini Embeddings</small></div>
                </div>
              </section>

              {/* 3 AI Agents Breakdown */}
              <section className={styles.section}>
                <h3><FaRobot /> The 3 Specialized AI Agents</h3>
                <div className={styles.agentGrid}>
                  
                  <div className={styles.agentCard}>
                    <div className={styles.agentIcon}><FaRobot/></div>
                    <h4>ParserAgent</h4>
                    <p>Extracts structured data from unstructured resume text using FewShotPromptTemplate.</p>
                    <ul>
                      <li><FaCheckCircle className={styles.checkIcon}/> Skill Extraction</li>
                      <li><FaCheckCircle className={styles.checkIcon}/> Tech Classification</li>
                      <li><FaCheckCircle className={styles.checkIcon}/> JSON Output Format</li>
                    </ul>
                  </div>

                  <div className={styles.agentCard}>
                    <div className={styles.agentIcon}><FaCogs/></div>
                    <h4>ATSAgent</h4>
                    <p>Simulates Applicant Tracking System scoring against specific job descriptions.</p>
                    <ul>
                      <li><FaCheckCircle className={styles.checkIcon}/> Keyword Match</li>
                      <li><FaCheckCircle className={styles.checkIcon}/> Experience Relevance</li>
                      <li><FaCheckCircle className={styles.checkIcon}/> Formatting Check</li>
                    </ul>
                  </div>

                  <div className={styles.agentCard}>
                    <div className={styles.agentIcon}><FaRoute/></div>
                    <h4>FeedbackAgent</h4>
                    <p>Transforms the ATS analysis into actionable career improvement strategies.</p>
                    <ul>
                      <li><FaCheckCircle className={styles.checkIcon}/> Skill Gap Analysis</li>
                      <li><FaCheckCircle className={styles.checkIcon}/> Resume Fixes</li>
                      <li><FaCheckCircle className={styles.checkIcon}/> 30/60/90 Day Roadmap</li>
                    </ul>
                  </div>

                </div>
              </section>

              {/* RAG Section */}
              <section className={styles.section}>
                <h3><FaDatabase /> Retrieval Augmented Generation (RAG)</h3>
                <div className={styles.ragBox}>
                  <p style={{marginBottom: "1rem"}}>Resumia improves ATS accuracy using industry job description context.</p>
                  <div className={styles.ragFlow}>
                    <span>Resume Skills</span> ➔ <span>Gemini Embedded</span> ➔ <span>FAISS Search</span> ➔ <span>Context Applied &amp; Evaluated</span>
                  </div>
                </div>
              </section>

              {/* Stack */}
              <section className={styles.section}>
                <h3><FaCodeBranch /> Technology Stack</h3>
                <div className={styles.techStack}>
                  <span className={styles.techTag}>React 18</span>
                  <span className={styles.techTag}>FastAPI</span>
                  <span className={styles.techTag}>LangChain</span>
                  <span className={styles.techTag}>FAISS Database</span>
                  <span className={styles.techTag}>Gemini LLM API</span>
                  <span className={styles.techTag}>Python</span>
                </div>
              </section>

              <div className={styles.modalFooter}>
                <a href="https://github.com/princedesai012/Resumia.git" target="_blank" rel="noreferrer" className={styles.footerBtn}>
                  <FaGithub /> View Repository
                </a>
              </div>

            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default CaseStudyModal;
